import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import bkstApi from "../../api";
import BTouchDatePicker from "../../common/input/BTouchDatePicker";
import { getItemProgress, getQueryParam, mergeItemProgress, sortByOrderDateTime } from "../../common/util";
import ItemsView from "../../order/screen/ItemsView";
import { UserContext } from "../../context/UserContext";
import ProductionTrackerItemDialog from "../components/ProductionTrackerItemDialog";

const TRACKER_MAX_PAST_DAYS = 30;

function trackerMinDateStr() {
  return moment().subtract(TRACKER_MAX_PAST_DAYS, "days").format("YYYY-MM-DD");
}

function clampTrackerDate(dateStr) {
  if (!dateStr || !moment(dateStr, "YYYY-MM-DD", true).isValid()) {
    return moment().format("YYYY-MM-DD");
  }
  const min = trackerMinDateStr();
  return moment(dateStr, "YYYY-MM-DD").isBefore(min, "day") ? min : dateStr;
}

function resolveTrackerDate(search) {
  const dateParam = getQueryParam(search, "date");
  if (dateParam && moment(dateParam, "YYYY-MM-DD", true).isValid()) {
    return clampTrackerDate(dateParam);
  }

  const searchBy = getQueryParam(search, "search");
  if (searchBy === "date") {
    const from = getQueryParam(search, "from");
    const to = getQueryParam(search, "to");
    if (from && to && from === to && moment(from, "YYYY-MM-DD", true).isValid()) {
      return clampTrackerDate(from);
    }
  }

  return moment().format("YYYY-MM-DD");
}

function formatDaysFromToday(dateStr) {
  const diff = moment(dateStr, "YYYY-MM-DD").startOf("day").diff(moment().startOf("day"), "days");
  if (diff === 0) {
    return "Today";
  }
  if (diff === 1) {
    return "Tomorrow";
  }
  if (diff === -1) {
    return "Yesterday";
  }
  if (diff > 1) {
    return `${diff} days away`;
  }
  return `${Math.abs(diff)} days ago`;
}

function buildItemRows(orders, progressSteps) {
  let rows = [];
  for (const o of orders) {
    (o.items || []).forEach((item, itemIndex) => {
      rows.push({
        ...item,
        itemIndex,
        itemCount: o.items.length,
        progress: getItemProgress(o, item, progressSteps),
        fulfillment: o.fulfillment,
        customer: o.customer,
        orderId: o.id,
      });
    });
  }
  return rows;
}

// Kitchen Dashboard (KDS mode). The standard Orders screen stays in Orders.js.
export default function ProductionTracker(props) {
  const navigate = useNavigate();
  let location = useLocation();
  const { user } = useContext(UserContext);
  const progressSteps = user?.config?.kds?.progressSteps;

  const [selectedDate, setSelectedDate] = useState(() => resolveTrackerDate(location.search));
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [dataPulledAt, setDataPulledAt] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const items = useMemo(() => buildItemRows(orders, progressSteps), [orders, progressSteps]);

  const loadTrackerData = useCallback((date) => {
    setFetching(true);
    return bkstApi
      .get(`/my/production/tracker?date=${date}`)
      .then((res) => {
        const arr = (res.data.orders || []).sort(sortByOrderDateTime);
        setOrders(arr);
        setDataPulledAt(Date.now());
      })
      .catch(() => {
        setOrders([]);
        setDataPulledAt(Date.now());
      })
      .finally(() => {
        setFetching(false);
      });
  }, []);

  useEffect(() => {
    const date = resolveTrackerDate(location.search);
    const rawDateParam = getQueryParam(location.search, "date");
    if (!rawDateParam || rawDateParam !== date) {
      navigate(`/production/tracker?date=${date}`, { replace: true });
      return;
    }
    setFetching(true);
    setSelectedDate(date);
    try {
      sessionStorage.setItem("productionTrackerReturn", `/production/tracker?date=${date}`);
    } catch {
      // ignore private mode / storage errors
    }
    loadTrackerData(date);
  }, [location.key, loadTrackerData, navigate]);

  const handleDateChange = (date) => {
    if (!date) {
      return;
    }
    navigate(`/production/tracker?date=${clampTrackerDate(date)}`);
  };

  const trackerMinDate = moment().subtract(TRACKER_MAX_PAST_DAYS, "days").startOf("day").toDate();

  const handleRefresh = () => {
    loadTrackerData(selectedDate);
  };

  const handleItemProgressChange = (orderId, { items }) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, items: mergeItemProgress(o.items, items) } : o))
    );
  };

  const handleItemSelect = (item) => {
    setSelectedItem({ orderId: item.orderId, itemIndex: item.itemIndex });
  };

  const handleCloseItemDialog = useCallback(() => {
    setSelectedItem(null);
  }, []);

  return (
    <>
      <ProductionTrackerItemDialog
        open={Boolean(selectedItem)}
        orderId={selectedItem?.orderId}
        itemIndex={selectedItem?.itemIndex}
        onClose={handleCloseItemDialog}
        onFocusItemChange={(itemIndex) =>
          setSelectedItem((prev) => (prev ? { ...prev, itemIndex } : prev))
        }
        onItemProgressChange={handleItemProgressChange}
      />
      <Backdrop
        open={fetching}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="lg">
        <Box mt={4} mb={2} sx={{ maxWidth: "90vw" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <BTouchDatePicker
                inline
                inlineVariant="h2"
                hideInlineIcon
                label="Date"
                value={selectedDate}
                onChange={handleDateChange}
                inlineLabel={moment(selectedDate, "YYYY-MM-DD").format("dddd, MMM D, YYYY")}
                inlineActionLabel="change"
                minDate={trackerMinDate}
              />
              <Typography variant="body1" color="textSecondary" sx={{ mt: 1, mb: 0, lineHeight: 1.3 }}>
                {formatDaysFromToday(selectedDate)}
                {dataPulledAt && (
                  <>
                    {" • "}
                    Last refreshed at {moment(dataPulledAt).format("h:mm A")}
                  </>
                )}
              </Typography>
            </Box>
            <IconButton
              aria-label="Refresh"
              onClick={handleRefresh}
              disabled={fetching}
              size="large"
              sx={{ color: "text.secondary", flexShrink: 0, mt: 0.25 }}
            >
              <RefreshIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Box>
        <ItemsView
          data={fetching ? [] : items}
          onItemProgressChange={handleItemProgressChange}
          onItemSelect={handleItemSelect}
          swimLanes
          hideDateSummary
        />
      </Container>
    </>
  );
}
